import { getAuth } from 'firebase/auth';
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, getFirestore, increment, onSnapshot, orderBy, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Checkout from '../Components/Checkout';
import { app } from '../firebase';
import './SocialFeed.css';

const auth = getAuth(app);
const db = getFirestore(app);

const SocialFeed = () => {
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [user, setUser] = useState(() => auth.currentUser);
    const [likedPosts, setLikedPosts] = useState(new Set());
    const [commentingPost, setCommentingPost] = useState(null);
    const [commentText, setCommentText] = useState('');
    const [showComments, setShowComments] = useState(new Set());
    const [shareFeedback, setShareFeedback] = useState(null);
    const [showCreatePost, setShowCreatePost] = useState(false);
    const [newPostContent, setNewPostContent] = useState('');
    const [selectedTrack, setSelectedTrack] = useState(null);
    const [availableTracks, setAvailableTracks] = useState([]);
    const [playingTrack, setPlayingTrack] = useState(null);
    const [audioRef, setAudioRef] = useState(null);
    const [showTrackSelection, setShowTrackSelection] = useState(false);
    const [userProfiles, setUserProfiles] = useState({});
    const [checkoutTrack, setCheckoutTrack] = useState(null);
    const [filterFollowing, setFilterFollowing] = useState(false);
    const [currentUserFollowing, setCurrentUserFollowing] = useState([]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(setUser);
        return unsubscribe;
    }, []);

    useEffect(() => {
        if (user) {
            fetchCurrentUserFollowing();
        }
    }, [user]);

    useEffect(() => {
        // Set up real-time listener for posts
        const q = query(collection(db, 'socialFeed'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, 
            (snapshot) => {
                const fetchedPosts = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setPosts(fetchedPosts);
                setLoading(false);
                
                // Fetch user profiles for all posts
                fetchUserProfiles(fetchedPosts);
            },
            (err) => {
                console.error('Error listening to posts:', err);
                setError('Failed to load feed.');
                setLoading(false);
                // Fallback to one-time fetch
                fetchPosts();
            }
        );

        return () => unsubscribe();
    }, []);

    const fetchCurrentUserFollowing = async () => {
        if (!user) return;
        try {
            const profileDoc = await getDoc(doc(db, 'profiles', user.uid));
            if (profileDoc.exists()) {
                const following = profileDoc.data().following || [];
                setCurrentUserFollowing(Array.isArray(following) ? following : []);
            }
        } catch (err) {
            console.error('Error fetching following list:', err);
        }
    };

    const fetchUserProfiles = async (posts) => {
        const userIds = new Set();
        posts.forEach(post => {
            if (post.user?.id) {
                userIds.add(post.user.id);
            }
        });

        const profiles = {};
        await Promise.all(
            Array.from(userIds).map(async (userId) => {
                try {
                    const profileDoc = await getDoc(doc(db, 'profiles', userId));
                    if (profileDoc.exists()) {
                        const profileData = profileDoc.data();
                        const profile = profileData.profile || {};
                        profiles[userId] = {
                            name: profile.name || 'Unknown User',
                            avatar: profile.profilePicture || '',
                        };
                    }
                } catch (err) {
                    console.error(`Error fetching profile for ${userId}:`, err);
                }
            })
        );
        setUserProfiles(profiles);
    };

    const fetchPosts = async () => {
        setLoading(true);
        setError('');
        try {
            const q = query(collection(db, 'socialFeed'), orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            const fetchedPosts = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setPosts(fetchedPosts);
            fetchUserProfiles(fetchedPosts);
        } catch (err) {
            console.error('Error fetching posts:', err);
            setError('Failed to load feed.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePost = async (postId) => {
        if (!user) return;
        if (!window.confirm('Are you sure you want to delete this post?')) return;

        try {
            const post = posts.find(p => p.id === postId);
            if (post && post.user?.id === user.uid) {
                await deleteDoc(doc(db, 'socialFeed', postId));
                // Post will be removed automatically by the real-time listener
            }
        } catch (err) {
            console.error('Error deleting post:', err);
            setError('Failed to delete post.');
        }
    };

    const getDemoPosts = () => {
        return [
            {
                id: 1,
                user: {
                    name: 'DJ Chill',
                    avatar: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100&h=100&fit=crop',
                    id: '1'
                },
                type: 'upload',
                content: 'Just uploaded a new track!',
                track: {
                    title: 'Chill Vibes',
                    coverImage: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop',
                    genre: 'Lo-Fi',
                    id: 'track1'
                },
                likes: 24,
                comments: [
                    { id: 1, user: 'User1', text: 'Amazing track!', time: new Date(Date.now() - 1800000).toISOString() },
                    { id: 2, user: 'User2', text: 'Love it!', time: new Date(Date.now() - 3600000).toISOString() }
                ],
                createdAt: new Date(Date.now() - 3600000).toISOString(),
            },
            {
                id: 2,
                user: {
                    name: 'Beat Master',
                    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
                    id: '2'
                },
                type: 'like',
                content: 'liked your track',
                track: {
                    title: 'Party Mix',
                    coverImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
                    genre: 'Electronic',
                    id: 'track2'
                },
                likes: 12,
                comments: [],
                createdAt: new Date(Date.now() - 7200000).toISOString(),
            },
            {
                id: 3,
                user: {
                    name: 'Groove King',
                    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
                    id: '3'
                },
                type: 'post',
                content: 'Working on something special. Stay tuned! 🎵',
                track: null,
                likes: 45,
                comments: [
                    { id: 1, user: 'User3', text: 'Can\'t wait!', time: new Date(Date.now() - 5400000).toISOString() }
                ],
                createdAt: new Date(Date.now() - 10800000).toISOString(),
            },
        ];
    };

    const handleLike = async (postId) => {
        if (!user) {
            setError('Please log in to like posts.');
            return;
        }

        const isLiked = likedPosts.has(postId);
        const newLikedPosts = new Set(likedPosts);
        
        // Update local state immediately for better UX
        setPosts(prevPosts => prevPosts.map(post => {
            if (post.id === postId) {
                return {
                    ...post,
                    likes: isLiked ? (post.likes || 1) - 1 : (post.likes || 0) + 1
                };
            }
            return post;
        }));

        if (isLiked) {
            newLikedPosts.delete(postId);
        } else {
            newLikedPosts.add(postId);
        }
        setLikedPosts(newLikedPosts);

        // Try to update in Firestore
        try {
            const postRef = doc(db, 'socialFeed', postId);
            await updateDoc(postRef, {
                likes: increment(isLiked ? -1 : 1)
            });
        } catch (err) {
            console.error('Error updating like:', err);
            // Revert if Firestore update fails
            setPosts(prevPosts => prevPosts.map(post => {
                if (post.id === postId) {
                    return {
                        ...post,
                        likes: isLiked ? (post.likes || 0) + 1 : (post.likes || 1) - 1
                    };
                }
                return post;
            }));
            if (isLiked) {
                newLikedPosts.add(postId);
            } else {
                newLikedPosts.delete(postId);
            }
            setLikedPosts(newLikedPosts);
        }
    };

    const handleComment = (postId) => {
        if (!user) {
            setError('Please log in to comment.');
            return;
        }
        setCommentingPost(postId);
        setShowComments(prev => {
            const newSet = new Set(prev);
            newSet.add(postId);
            return newSet;
        });
    };

    const handleSubmitComment = async (e, postId) => {
        e.preventDefault();
        if (!commentText.trim() || !user) return;

        const newComment = {
            id: Date.now(),
            user: user.displayName || user.email || 'You',
            text: commentText.trim(),
            time: new Date().toISOString()
        };

        // Update local state immediately
        setPosts(prevPosts => prevPosts.map(post => {
            if (post.id === postId) {
                return {
                    ...post,
                    comments: [...(post.comments || []), newComment]
                };
            }
            return post;
        }));

        setCommentText('');
        setCommentingPost(null);

        // Try to update in Firestore
        try {
            const postRef = doc(db, 'socialFeed', postId);
            const post = posts.find(p => p.id === postId);
            await updateDoc(postRef, {
                comments: [...(post.comments || []), newComment]
            });
        } catch (err) {
            console.error('Error adding comment:', err);
        }
    };

    const handleShare = async (post) => {
        const postUrl = `${window.location.origin}/feed?post=${post.id}`;
        
        // Try native share API first
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `${post.user.name} on Tune`,
                    text: post.content,
                    url: postUrl
                });
                showShareFeedback('Shared successfully!');
            } catch (err) {
                if (err.name !== 'AbortError') {
                    // Fallback to clipboard
                    copyToClipboard(postUrl);
                }
            }
        } else {
            // Fallback to clipboard
            copyToClipboard(postUrl);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            showShareFeedback('Link copied to clipboard!');
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                showShareFeedback('Link copied to clipboard!');
            } catch (err) {
                setError('Failed to copy link.');
            }
            document.body.removeChild(textArea);
        }
    };

    const showShareFeedback = (message) => {
        setShareFeedback(message);
        setTimeout(() => setShareFeedback(null), 3000);
    };

    const toggleComments = (postId) => {
        setShowComments(prev => {
            const newSet = new Set(prev);
            if (newSet.has(postId)) {
                newSet.delete(postId);
            } else {
                newSet.add(postId);
            }
            return newSet;
        });
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);
        
        if (seconds < 60) return 'just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        if (days < 7) return `${days}d ago`;
        const weeks = Math.floor(days / 7);
        if (weeks < 4) return `${weeks}w ago`;
        const months = Math.floor(days / 30);
        return `${months}mo ago`;
    };

    const getPostIcon = (type) => {
        switch (type) {
            case 'upload':
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                );
            case 'like':
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                );
            default:
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                );
        }
    };

    return (
        <div className="social-feed-page">
            <div className="container">
                {/* Header */}
                <div className="feed-header">
                    <div>
                        <h1 className="page-title">
                            Social <span className="gradient-text">Feed</span>
                        </h1>
                        <p className="page-subtitle">
                            See what artists and producers are up to
                        </p>
                    </div>
                    {user && (
                        <button 
                            className="create-post-button"
                            onClick={() => setShowCreatePost(true)}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"/>
                                <line x1="5" y1="12" x2="19" y2="12"/>
                            </svg>
                            Create Post
                        </button>
                    )}
                </div>

                {/* Share Feedback Toast */}
                {shareFeedback && (
                    <div className="toast-message success-toast">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                        <span>{shareFeedback}</span>
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading feed...</p>
                    </div>
                )}

                {/* Error Message */}
                {error && !loading && (
                    <div className="message error-message">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        <span>{error}</span>
                        <button 
                            className="error-close"
                            onClick={() => setError('')}
                            aria-label="Close error"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>
                )}

                {/* Filter Toggle */}
                {user && currentUserFollowing.length > 0 && (
                    <div className="feed-filter">
                        <button
                            className={`filter-btn ${filterFollowing ? 'active' : ''}`}
                            onClick={() => setFilterFollowing(!filterFollowing)}
                        >
                            {filterFollowing ? 'Show All' : 'Following Only'}
                        </button>
                    </div>
                )}

                {/* Feed Posts */}
                {!loading && (
                    <div className="feed-container">
                        {(filterFollowing && user 
                            ? posts.filter(post => currentUserFollowing.includes(post.user?.id))
                            : posts
                        ).length === 0 ? (
                            <div className="empty-state">
                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                                <h3>No posts yet</h3>
                                <p>Be the first to share something!</p>
                            </div>
                        ) : (
                            <div className="posts-timeline">
                                {(filterFollowing && user 
                                    ? posts.filter(post => currentUserFollowing.includes(post.user?.id))
                                    : posts
                                ).map((post) => {
                                    const isLiked = likedPosts.has(post.id);
                                    const postUser = post.user || { name: 'Unknown User', avatar: '', id: null };
                                    // Use fetched profile if available
                                    const userProfile = postUser.id && userProfiles[postUser.id] 
                                        ? { ...postUser, ...userProfiles[postUser.id] }
                                        : postUser;
                                    const commentsVisible = showComments.has(post.id);
                                    const isCommenting = commentingPost === post.id;
                                    const isOwnPost = user && user.uid === postUser.id;
                                    
                                    return (
                                        <article key={post.id} className="feed-post">
                                            <div className="post-header">
                                                <Link 
                                                    to={userProfile.id ? `/artists/${userProfile.id}` : '#'} 
                                                    className="user-avatar"
                                                >
                                                    {userProfile.avatar ? (
                                                        <img src={userProfile.avatar} alt={userProfile.name} />
                                                    ) : (
                                                        <div className="avatar-placeholder">
                                                            {userProfile.name.charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                </Link>
                                                <div className="user-info">
                                                    <Link 
                                                        to={userProfile.id ? `/artists/${userProfile.id}` : '#'} 
                                                        className="user-name"
                                                    >
                                                        {userProfile.name}
                                                    </Link>
                                                    <span className="post-time">
                                                        {formatTimeAgo(
                                                            post.createdAt?.toDate 
                                                                ? post.createdAt.toDate().toISOString()
                                                                : post.createdAt?.seconds
                                                                ? new Date(post.createdAt.seconds * 1000).toISOString()
                                                                : post.createdAt || new Date().toISOString()
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="post-header-actions">
                                                    <div className="post-type-icon">
                                                        {getPostIcon(post.type)}
                                                    </div>
                                                    {isOwnPost && (
                                                        <button
                                                            className="delete-post-btn"
                                                            onClick={() => handleDeletePost(post.id)}
                                                            aria-label="Delete post"
                                                        >
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <polyline points="3 6 5 6 21 6"/>
                                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="post-content">
                                                <p>{post.content}</p>
                                                {post.track && (
                                                    <div className="track-preview-card">
                                                        <div className="track-preview-cover">
                                                            {post.track.coverImage ? (
                                                                <img src={post.track.coverImage} alt={post.track.title} />
                                                            ) : (
                                                                <div className="track-cover-placeholder"></div>
                                                            )}
                                                            {post.track.fileUrl && (
                                                                <div className="track-play-overlay">
                                                                    <button 
                                                                        className="track-play-btn"
                                                                        onClick={() => {
                                                                            if (playingTrack === post.track.id) {
                                                                                if (audioRef) {
                                                                                    audioRef.pause();
                                                                                    setPlayingTrack(null);
                                                                                }
                                                                            } else {
                                                                                if (audioRef) audioRef.pause();
                                                                                const newAudio = new Audio(post.track.fileUrl);
                                                                                newAudio.play();
                                                                                setPlayingTrack(post.track.id);
                                                                                setAudioRef(newAudio);
                                                                                newAudio.onended = () => setPlayingTrack(null);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                                                            {playingTrack === post.track.id ? (
                                                                                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                                                                            ) : (
                                                                                <path d="M8 5v14l11-7z"/>
                                                                            )}
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="track-preview-info">
                                                            <Link to={`/marketplace`} className="track-preview-link">
                                                                <h4>{post.track.title}</h4>
                                                                {post.track.genre && (
                                                                    <span className="track-genre-badge">{post.track.genre}</span>
                                                                )}
                                                                {post.track.price && (
                                                                    <span className="track-price">${post.track.price ? (typeof post.track.price === 'number' ? post.track.price.toFixed(2) : parseFloat(post.track.price).toFixed(2)) : '0.00'}</span>
                                                                )}
                                                            </Link>
                                                            {user && user.uid !== post.track.userId && (
                                                                <button
                                                                    className="buy-track-btn-small"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        setCheckoutTrack(post.track);
                                                                    }}
                                                                >
                                                                    Buy Now
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="post-actions">
                                                <button
                                                    className={`action-button like-button ${isLiked ? 'liked' : ''}`}
                                                    onClick={() => handleLike(post.id)}
                                                    aria-label={isLiked ? 'Unlike post' : 'Like post'}
                                                >
                                                    <svg 
                                                        width="20" 
                                                        height="20" 
                                                        viewBox="0 0 24 24" 
                                                        fill={isLiked ? "currentColor" : "none"} 
                                                        stroke="currentColor" 
                                                        strokeWidth="2"
                                                    >
                                                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                                    </svg>
                                                    <span>{post.likes || 0}</span>
                                                </button>
                                                <button 
                                                    className="action-button comment-button"
                                                    onClick={() => toggleComments(post.id)}
                                                    aria-label="View comments"
                                                >
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                                    </svg>
                                                    <span>{post.comments?.length || 0}</span>
                                                </button>
                                                <button 
                                                    className="action-button share-button"
                                                    onClick={() => handleShare(post)}
                                                    aria-label="Share post"
                                                >
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="18" cy="5" r="3"/>
                                                        <circle cx="6" cy="12" r="3"/>
                                                        <circle cx="18" cy="19" r="3"/>
                                                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                                                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                                                    </svg>
                                                    <span>Share</span>
                                                </button>
                                            </div>

                                            {/* Comments Section */}
                                            {commentsVisible && (
                                                <div className="comments-section">
                                                    {post.comments && post.comments.length > 0 && (
                                                        <div className="comments-list">
                                                            {post.comments.map((comment) => (
                                                                <div key={comment.id} className="comment-item">
                                                                    <div className="comment-avatar">
                                                        {comment.user.charAt(0).toUpperCase()}
                                                    </div>
                                                                    <div className="comment-content">
                                                                        <div className="comment-header">
                                                                            <span className="comment-user">{comment.user}</span>
                                                                            <span className="comment-time">{formatTimeAgo(comment.time)}</span>
                                                                        </div>
                                                                        <p className="comment-text">{comment.text}</p>
                                                                    </div>
                    </div>
                ))}
                                                        </div>
                                                    )}
                                                    {isCommenting ? (
                                                        <form 
                                                            className="comment-form"
                                                            onSubmit={(e) => handleSubmitComment(e, post.id)}
                                                        >
                                                            <input
                                                                type="text"
                                                                value={commentText}
                                                                onChange={(e) => setCommentText(e.target.value)}
                                                                placeholder="Write a comment..."
                                                                className="comment-input"
                                                                autoFocus
                                                            />
                                                            <div className="comment-form-actions">
                                                                <button
                                                                    type="button"
                                                                    className="cancel-comment-btn"
                                                                    onClick={() => {
                                                                        setCommentingPost(null);
                                                                        setCommentText('');
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    type="submit"
                                                                    className="submit-comment-btn"
                                                                    disabled={!commentText.trim()}
                                                                >
                                                                    Post
                                                                </button>
                                                            </div>
                                                        </form>
                                                    ) : (
                                                        <button
                                                            className="add-comment-btn"
                                                            onClick={() => handleComment(post.id)}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <line x1="12" y1="5" x2="12" y2="19"/>
                                                                <line x1="5" y1="12" x2="19" y2="12"/>
                                                            </svg>
                                                            Add a comment
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Create Post Modal */}
                {showCreatePost && (
                    <div className="modal-overlay" onClick={() => setShowCreatePost(false)}>
                        <div className="modal-content create-post-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Create Post</h2>
                                <button
                                    className="modal-close"
                                    onClick={() => {
                                        setShowCreatePost(false);
                                        setNewPostContent('');
                                        setSelectedTrack(null);
                                    }}
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            </div>
                            <form 
                                className="create-post-form"
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    if (!newPostContent.trim() || !user) return;
                                    
                                    try {
                                        // Fetch user profile for better display
                                        let userName = user.displayName || user.email || 'You';
                                        let userAvatar = user.photoURL || '';
                                        try {
                                            const profileDoc = await getDoc(doc(db, 'profiles', user.uid));
                                            if (profileDoc.exists()) {
                                                const profile = profileDoc.data().profile || {};
                                                userName = profile.name || userName;
                                                userAvatar = profile.profilePicture || userAvatar;
                                            }
                                        } catch (err) {
                                            console.error('Error fetching profile:', err);
                                        }

                                        const postData = {
                                            user: {
                                                name: userName,
                                                avatar: userAvatar,
                                                id: user.uid
                                            },
                                            type: selectedTrack ? 'upload' : 'post',
                                            content: newPostContent.trim(),
                                            track: selectedTrack || null,
                                            likes: 0,
                                            comments: [],
                                            createdAt: Timestamp.now()
                                        };
                                        
                                        await addDoc(collection(db, 'socialFeed'), postData);
                                        setNewPostContent('');
                                        setSelectedTrack(null);
                                        setShowCreatePost(false);
                                        fetchPosts();
                                    } catch (err) {
                                        console.error('Error creating post:', err);
                                        setError('Failed to create post.');
                                    }
                                }}
                            >
                                <div className="form-group">
                                    <textarea
                                        className="post-content-input"
                                        value={newPostContent}
                                        onChange={(e) => setNewPostContent(e.target.value)}
                                        placeholder="What's on your mind?"
                                        rows="4"
                                        required
                                    />
                                </div>
                                
                                {selectedTrack && (
                                    <div className="selected-track-preview">
                                        <div className="selected-track-cover">
                                            {selectedTrack.coverImage ? (
                                                <img src={selectedTrack.coverImage} alt={selectedTrack.title} />
                                            ) : (
                                                <div className="track-cover-placeholder"></div>
                                            )}
                                        </div>
                                        <div className="selected-track-info">
                                            <h4>{selectedTrack.title}</h4>
                                            {selectedTrack.genre && (
                                                <span className="track-genre-badge">{selectedTrack.genre}</span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="remove-track-btn"
                                            onClick={() => setSelectedTrack(null)}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="18" y1="6" x2="6" y2="18"/>
                                                <line x1="6" y1="6" x2="18" y2="18"/>
                                            </svg>
                                        </button>
                                    </div>
                                )}

                                {!selectedTrack && (
                                    <button
                                        type="button"
                                        className="attach-track-btn"
                                        onClick={async () => {
                                            try {
                                                const tracksQuery = query(
                                                    collection(db, 'tracks'),
                                                    where('userId', '==', user.uid),
                                                    orderBy('uploadedAt', 'desc')
                                                );
                                                const tracksSnapshot = await getDocs(tracksQuery);
                                                const userTracks = tracksSnapshot.docs.map(doc => ({
                                                    id: doc.id,
                                                    ...doc.data()
                                                }));
                                                setAvailableTracks(userTracks);
                                                
                                                if (userTracks.length > 0) {
                                                    setShowTrackSelection(true);
                                                } else {
                                                    setError('You need to upload tracks first. Go to Upload page.');
                                                }
                                            } catch (err) {
                                                console.error('Error fetching tracks:', err);
                                                setError('Failed to load your tracks.');
                                            }
                                        }}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                            <polyline points="17 8 12 3 7 8"/>
                                            <line x1="12" y1="3" x2="12" y2="15"/>
                                        </svg>
                                        Attach Track
                                    </button>
                                )}

                                <div className="modal-actions">
                                    <button
                                        type="button"
                                        className="cancel-button"
                                        onClick={() => {
                                            setShowCreatePost(false);
                                            setNewPostContent('');
                                            setSelectedTrack(null);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="submit-button"
                                        disabled={!newPostContent.trim()}
                                    >
                                        Post
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Track Selection Modal */}
                {showTrackSelection && (
                    <div className="modal-overlay" onClick={() => setShowTrackSelection(false)}>
                        <div className="modal-content track-selection-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Select Track to Attach</h2>
                                <button
                                    className="modal-close"
                                    onClick={() => setShowTrackSelection(false)}
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            </div>
                            <div className="tracks-selection-list">
                                {availableTracks.length === 0 ? (
                                    <div className="empty-state">
                                        <p>No tracks available. Upload tracks first.</p>
                                    </div>
                                ) : (
                                    availableTracks.map((track) => (
                                        <div
                                            key={track.id}
                                            className="track-selection-item"
                                            onClick={() => {
                                                setSelectedTrack(track);
                                                setShowTrackSelection(false);
                                            }}
                                        >
                                            <div className="track-selection-cover">
                                                {track.coverImage ? (
                                                    <img src={track.coverImage} alt={track.title} />
                                                ) : (
                                                    <div className="track-cover-placeholder"></div>
                                                )}
                                            </div>
                                            <div className="track-selection-info">
                                                <h4>{track.title || 'Untitled'}</h4>
                                                {track.genre && (
                                                    <span className="track-genre-badge">{track.genre}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Checkout Modal */}
                {checkoutTrack && (
                    <Checkout
                        track={checkoutTrack}
                        onSuccess={() => {
                            setCheckoutTrack(null);
                        }}
                        onCancel={() => setCheckoutTrack(null)}
                    />
                )}
            </div>
        </div>
    );
};

export default SocialFeed;
